if (!customElements.get('multicolumn-buy-all')) {
  customElements.define(
    'multicolumn-buy-all',
    class MulticolumnBuyAll extends HTMLElement {
      connectedCallback() {
        if (!this.initialized) {
          this.section = this.closest('.multicolumn');
          this.button = this.querySelector('button');
          this.label = this.querySelector('[data-buy-all-label]');
          this.spinner = this.querySelector('.loading-overlay__spinner');
          this.errorMessage = this.querySelector('[role="alert"]');

          if (!this.section || !this.button || !this.label || !this.spinner || !this.errorMessage) return;

          this.initialized = true;
          this.button.addEventListener('click', () => this.addAllToCart());
        }

        this.observer?.disconnect();

        if (window.Shopify?.designMode) {
          this.resolveProducts();
        } else if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;

              this.observer.disconnect();
              this.resolveProducts();
            },
            { rootMargin: '0px 0px 800px 0px' }
          );
          this.observer.observe(this.section);
        } else {
          this.resolveProducts();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      getProductReferences() {
        const references = new Map();

        this.section.querySelectorAll('.multicolumn-card__info .rte a[href]').forEach((anchor) => {
          try {
            const url = new URL(anchor.href, window.location.href);
            const productPathMatch = url.pathname.match(/(^|\/)products\/([^/]+)\/?$/);
            const isThemeEditor = Boolean(window.Shopify?.designMode);

            if ((!isThemeEditor && url.origin !== window.location.origin) || !productPathMatch) return;

            const productPath = url.pathname.replace(/\/$/, '');
            const requestedVariantId = url.searchParams.get('variant');
            const referenceKey = `${productPath}:${requestedVariantId || ''}`;

            if (!references.has(referenceKey)) {
              references.set(referenceKey, {
                productUrl: `${window.location.origin}${productPath}.js`,
                requestedVariantId,
              });
            }
          } catch (error) {
            // Ignore malformed or unsupported links and keep the original rich text usable.
          }
        });

        return Array.from(references.values());
      }

      async resolveProducts() {
        if (this.resolving) return;

        this.resolving = true;

        try {
          const references = this.getProductReferences();
          if (references.length < 2) return;

          const productRequests = new Map();
          const resolvedVariants = await Promise.all(
            references.map(async (reference) => {
              if (!productRequests.has(reference.productUrl)) {
                productRequests.set(
                  reference.productUrl,
                  fetch(reference.productUrl).then((response) => {
                    if (!response.ok) throw new Error();
                    return response.json();
                  })
                );
              }

              const product = await productRequests.get(reference.productUrl);
              if (!product || !Array.isArray(product.variants)) throw new Error();

              const variant = reference.requestedVariantId
                ? product.variants.find(
                    (candidate) =>
                      String(candidate.id) === reference.requestedVariantId && candidate.available
                  )
                : product.variants.find((candidate) => candidate.available);

              if (!variant || !Number.isFinite(Number(variant.price))) throw new Error();

              return {
                id: Number(variant.id),
                price: Number(variant.price),
              };
            })
          );

          const uniqueVariants = new Map();
          resolvedVariants.forEach((variant) => {
            if (!uniqueVariants.has(variant.id)) uniqueVariants.set(variant.id, variant);
          });

          this.variants = Array.from(uniqueVariants.values());
          if (this.variants.length < 2) return;
          if (!window.Shopify || typeof window.Shopify.formatMoney !== 'function') return;

          const total = this.variants.reduce((sum, variant) => sum + variant.price, 0);
          const count = String(this.variants.length);
          const labelTemplate = this.dataset.labelTemplate || '';
          const resolvedLabel = labelTemplate.replace(/\[count\]/gi, count);
          const formattedTotal = window.Shopify.formatMoney(total, this.dataset.moneyFormat);

          this.label.textContent = `${resolvedLabel} — ${formattedTotal}`;
          this.button.disabled = false;
          this.button.removeAttribute('aria-disabled');
          this.hidden = false;
        } catch (error) {
          // Fail closed when the complete referenced set cannot be resolved.
        } finally {
          this.resolving = false;
        }
      }

      async addAllToCart() {
        if (this.submitting || !this.variants || this.variants.length < 2) return;

        this.submitting = true;
        this.clearError();
        this.button.disabled = true;
        this.button.setAttribute('aria-disabled', 'true');
        this.button.setAttribute('aria-busy', 'true');
        this.button.classList.add('loading');
        this.spinner.classList.remove('hidden');

        try {
          const config = fetchConfig('javascript');
          delete config.headers.Accept;
          config.body = JSON.stringify({
            items: this.variants.map((variant) => ({ id: variant.id, quantity: 1 })),
          });

          const response = await fetch(`${window.routes.cart_add_url}.js`, config);
          const cartResponse = await response.json();

          if (!response.ok || cartResponse.status) {
            const message = cartResponse.description || window.cartStrings.error;
            const cartError = new Error(message);
            cartError.isCartResponseError = true;
            throw cartError;
          }

          window.location = window.routes.cart_url;
        } catch (error) {
          const message = error.isCartResponseError ? error.message : window.cartStrings.error;
          this.showError(message);
          this.restoreButton();
        }
      }

      clearError() {
        this.errorMessage.textContent = '';
        this.errorMessage.hidden = true;
      }

      showError(message) {
        if (!message) return;

        this.errorMessage.textContent = message;
        this.errorMessage.hidden = false;
      }

      restoreButton() {
        this.submitting = false;
        this.button.disabled = false;
        this.button.removeAttribute('aria-disabled');
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('loading');
        this.spinner.classList.add('hidden');
      }
    }
  );
}
